import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Engineering as EngineeringIcon,
  LocalGasStation as OilIcon,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const Dashboard = () => {
  const theme = useTheme();

  // Datos de ejemplo
  const statsCards = [
    {
      title: 'Empleados Activos',
      value: '284',
      change: '+12',
      changeType: 'positive',
      icon: <PeopleIcon />,
      color: theme.palette.primary.main,
    },
    {
      title: 'Obras Activas',
      value: '18',
      change: '+3',
      changeType: 'positive',
      icon: <OilIcon />,
      color: theme.palette.secondary.main,
    },
    {
      title: 'Facturación Mensual',
      value: '$42.5M',
      change: '+8.2%',
      changeType: 'positive',
      icon: <MoneyIcon />,
      color: theme.palette.success.main,
    },
    {
      title: 'Alertas Activas',
      value: '7',
      change: '-2',
      changeType: 'negative',
      icon: <WarningIcon />,
      color: theme.palette.error.main,
    },
  ];

  const revenueData = [
    { month: 'Jul', ingresos: 32, gastos: 24 },
    { month: 'Ago', ingresos: 35, gastos: 26 },
    { month: 'Sep', ingresos: 38, gastos: 28 },
    { month: 'Oct', ingresos: 42, gastos: 30 },
    { month: 'Nov', ingresos: 40, gastos: 29 },
    { month: 'Dic', ingresos: 45, gastos: 32 },
    { month: 'Ene', ingresos: 42.5, gastos: 31 },
  ];

  const projectsData = [
    { name: 'Pozo XY-123', progreso: 75, estado: 'En curso' },
    { name: 'Pozo AB-456', progreso: 45, estado: 'En curso' },
    { name: 'Workover MZ-789', progreso: 90, estado: 'Finalizando' },
    { name: 'Perforación NW-012', progreso: 20, estado: 'Iniciando' },
  ];

  const alertas = [
    { tipo: 'error', mensaje: 'Apto médico vencido - Pedro García', tiempo: 'Hace 2 horas' },
    { tipo: 'warning', mensaje: 'Capacitación H2S vence en 5 días - 3 empleados', tiempo: 'Hace 4 horas' },
    { tipo: 'warning', mensaje: 'Stock bajo de EPP - Cascos de seguridad', tiempo: 'Hace 6 horas' },
    { tipo: 'info', mensaje: 'Nueva orden de compra pendiente', tiempo: 'Hace 8 horas' },
  ];

  const pieData = [
    { name: 'Perforación', value: 35, color: theme.palette.primary.main },
    { name: 'Workover', value: 25, color: theme.palette.secondary.main },
    { name: 'Mantenimiento', value: 20, color: theme.palette.success.main },
    { name: 'Logística', value: 20, color: theme.palette.info.main },
  ];

  return (
    <Box className="fade-in">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} color="primary">
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Bienvenido al Sistema de Gestión Integral Petrolatina
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((card, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 25px ${alpha(card.color, 0.25)}`,
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(card.color, 0.1),
                      color: card.color,
                      width: 56,
                      height: 56,
                    }}
                  >
                    {card.icon}
                  </Avatar>
                  <Chip
                    size="small"
                    label={card.change}
                    icon={
                      card.changeType === 'positive' ? (
                        <TrendingUpIcon fontSize="small" />
                      ) : (
                        <TrendingDownIcon fontSize="small" />
                      )
                    }
                    sx={{
                      bgcolor: alpha(
                        card.changeType === 'positive'
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                        0.1
                      ),
                      color:
                        card.changeType === 'positive'
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Typography variant="h4" fontWeight={700}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue Chart */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Ingresos vs Gastos
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Últimos 7 meses (en millones ARS)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: theme.palette.primary.main }} />
                    <Typography variant="caption">Ingresos</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: theme.palette.secondary.main }} />
                    <Typography variant="caption">Gastos</Typography>
                  </Box>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorGastos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.secondary.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={theme.palette.secondary.main} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                  <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="ingresos"
                    stroke={theme.palette.primary.main}
                    fillOpacity={1}
                    fill="url(#colorIngresos)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="gastos"
                    stroke={theme.palette.secondary.main}
                    fillOpacity={1}
                    fill="url(#colorGastos)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Distribución por Tipo de Obra
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {pieData.map((item, index) => (
                  <Chip
                    key={index}
                    size="small"
                    label={`${item.name}: ${item.value}%`}
                    sx={{
                      bgcolor: alpha(item.color, 0.1),
                      color: item.color,
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Projects and Alerts Row */}
      <Grid container spacing={3}>
        {/* Active Projects */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  Proyectos Activos
                </Typography>
                <IconButton size="small" color="primary">
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {projectsData.map((project, index) => (
                  <Box key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EngineeringIcon color="primary" fontSize="small" />
                        <Typography variant="body2" fontWeight={500}>
                          {project.name}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={project.estado}
                        color={
                          project.estado === 'Finalizando'
                            ? 'success'
                            : project.estado === 'Iniciando'
                            ? 'info'
                            : 'primary'
                        }
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={project.progreso}
                        sx={{
                          flexGrow: 1,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }}
                      />
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 45 }}>
                        {project.progreso}%
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Alertas Recientes
                </Typography>
                <Chip
                  size="small"
                  label={`${alertas.length} activas`}
                  color="error"
                  variant="outlined"
                />
              </Box>
              <List disablePadding>
                {alertas.map((alerta, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: alpha(
                        alerta.tipo === 'error'
                          ? theme.palette.error.main
                          : alerta.tipo === 'warning'
                          ? theme.palette.warning.main
                          : theme.palette.info.main,
                        0.05
                      ),
                      borderLeft: `4px solid ${
                        alerta.tipo === 'error'
                          ? theme.palette.error.main
                          : alerta.tipo === 'warning'
                          ? theme.palette.warning.main
                          : theme.palette.info.main
                      }`,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: alpha(
                            alerta.tipo === 'error'
                              ? theme.palette.error.main
                              : alerta.tipo === 'warning'
                              ? theme.palette.warning.main
                              : theme.palette.info.main,
                            0.1
                          ),
                          color:
                            alerta.tipo === 'error'
                              ? theme.palette.error.main
                              : alerta.tipo === 'warning'
                              ? theme.palette.warning.main
                              : theme.palette.info.main,
                          width: 40,
                          height: 40,
                        }}
                      >
                        <WarningIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={alerta.mensaje}
                      secondary={alerta.tiempo}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
